import { MessageSquare } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";
import type { TFunction } from "i18next";

import { cn } from "~/shared/lib/utils";
import { Badge } from "~/shared/ui/badge";
import type { Asset } from "../types";
import { AssetThumb } from "./asset-media";
import { useOpenAssetChat } from "./use-asset-open";

interface AssetGridTileProps {
    t: TFunction;
    asset: Asset;
    /** Optional action rendered top-right (e.g. add-to-folder); clicks don't open the lightbox. */
    actionSlot?: ReactNode;
    /** Primary click on the tile → open the asset in the lightbox. */
    onOpenMedia?: (asset: Asset) => void;
}

/**
 * Dense-grid tile for the /assets library: square media poster + type badge. The whole tile
 * opens the lightbox; the source chat is reachable via the hover icon in the corner.
 */
export function AssetGridTile({ t, asset, actionSlot, onOpenMedia }: AssetGridTileProps) {
    const openChat = useOpenAssetChat();
    const openable = asset.chatUuid != null;

    const open = () => onOpenMedia?.(asset);
    const onKeyDown = (e: KeyboardEvent) => {
        if (onOpenMedia && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            open();
        }
    };

    return (
        <div
            role="button"
            tabIndex={onOpenMedia ? 0 : -1}
            onClick={open}
            onKeyDown={onKeyDown}
            className={cn(
                "group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted",
                onOpenMedia && "cursor-zoom-in",
            )}
        >
            <div className="absolute inset-0">
                <AssetThumb asset={asset} />
            </div>
            <div className="absolute top-1.5 left-1.5">
                <Badge variant="secondary" className="bg-black/60 font-normal text-white backdrop-blur">
                    {t(`assets:type.${asset.type}`)}
                </Badge>
            </div>
            {actionSlot ? (
                <div
                    role="presentation"
                    className="absolute top-1.5 right-1.5"
                    onClick={(e) => e.stopPropagation()}
                >
                    {actionSlot}
                </div>
            ) : null}
            {openable ? (
                <button
                    type="button"
                    aria-label={t("assets:lightbox.openChat")}
                    onClick={(e) => {
                        e.stopPropagation();
                        openChat(asset);
                    }}
                    className="absolute right-1.5 bottom-1.5 rounded-full bg-black/60 p-1.5 text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100 hover:bg-black/80 focus-visible:opacity-100"
                >
                    <MessageSquare className="size-4" />
                </button>
            ) : null}
        </div>
    );
}
